import React, { useCallback, useEffect } from "react";

import { isNil } from "lodash";
import { useFormContext, useWatch } from "react-hook-form";

import { FieldParameter } from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import {
  hasDataType,
  hasEnumData,
  hasPrimitiveData,
  isExpr,
} from "@fartherfinance/frontend/api/TaskWorkflows/utils/guards";

import { ExternalResources } from "../../core/types";
import { useGetFieldOptions } from "../../data/hooks/useGetFieldOptions";
import { resolveExpression } from "../../expressions/resolveExpressions";
import { useFieldDependencies } from "../hooks/useFieldDependencies";
import { resolveDefaultEnumValue } from "../utils/resolveDefaultEnumValue";

import FieldRenderer from "./FieldRenderer";

interface TemplateFieldProps {
  fieldParameter: FieldParameter;
  externalResources?: ExternalResources;
  fieldAnalysis?: ReturnType<
    typeof import("@fartherfinance/frontend/api/TaskWorkflows/utils/fieldHelpers").analyzeField
  >;
}

const TemplateField = ({
  fieldParameter,
  externalResources,
  fieldAnalysis: _fieldAnalysis,
}: TemplateFieldProps): JSX.Element => {
  const { getValues, resetField, setValue, control } = useFormContext();

  // Always call the hook to maintain hooks order, but with a flag for using results
  const {
    options: enumOptions,
    fetchOptionsData,
    isLoading,
  } = useGetFieldOptions(fieldParameter, getValues);

  const { dependencies } = useFieldDependencies(fieldParameter);

  // Only watch determinedBy fields for options refetching
  const determinedByValues = useWatch({
    control,
    name: dependencies.determinedBy.length > 0 ? dependencies.determinedBy : [],
  });

  useEffect(() => {
    if (
      !hasDataType(fieldParameter) ||
      !("defaultValue" in fieldParameter.dataType)
    ) {
      return;
    }

    const { dataType } = fieldParameter;
    const currentValue = getValues(fieldParameter.key);

    if (!isNil(currentValue)) {
      if (
        typeof currentValue === "object" &&
        "value" in currentValue &&
        currentValue.value !== undefined &&
        currentValue.value !== ""
      ) {
        return;
      } else if (
        typeof currentValue !== "object" &&
        (currentValue !== "" || typeof currentValue === "boolean")
      ) {
        return;
      } else if (Array.isArray(currentValue) && currentValue.length > 0) {
        return;
      }
    }

    if (hasEnumData(fieldParameter) && !isLoading && enumOptions.length > 0) {
      const defaultValue = resolveDefaultEnumValue(fieldParameter, enumOptions);
      if (defaultValue) {
        setValue(fieldParameter.key, defaultValue, {
          shouldValidate: true,
          shouldDirty: false,
          shouldTouch: true,
        });
      }
      return;
    }

    if (hasPrimitiveData(fieldParameter)) {
      const resolveAndSetDefault = async () => {
        try {
          const defaultValue = isExpr(dataType.defaultValue)
            ? await resolveExpression(
                dataType.defaultValue,
                getValues,
                externalResources
              )
            : dataType.defaultValue;

          if (defaultValue !== undefined) {
            setValue(fieldParameter.key, defaultValue, {
              shouldValidate: true,
              shouldDirty: false,
              shouldTouch: true,
            });
          }
        } catch (error) {
          console.warn(
            `[FormEngine:TemplateField] Error resolving default value for field ${fieldParameter.key}:`,
            error
          );
          // Set null as fallback to avoid broken state
          setValue(fieldParameter.key, null, {
            shouldValidate: true,
            shouldDirty: false,
            shouldTouch: true,
          });
        }
      };

      resolveAndSetDefault();
      return;
    }
  }, [
    fieldParameter,
    enumOptions,
    isLoading,
    setValue,
    getValues,
    externalResources,
  ]);

  // Use API guard to check if it's an enum data field type
  const isEnumDataField = hasEnumData(fieldParameter);

  const refetchAfterChange = useCallback(async () => {
    if (!fetchOptionsData) {
      return;
    }

    let isMounted = true;

    try {
      const newOptions = await fetchOptionsData();

      if (!isMounted) {
        return;
      }

      const currentValue = getValues(fieldParameter.key);
      const valueToCompare =
        typeof currentValue === "string" || typeof currentValue === "number"
          ? currentValue
          : currentValue?.value || currentValue?.id;

      const isValueNotInOptions =
        valueToCompare !== undefined &&
        !newOptions.find((option) => option.value === valueToCompare);

      if (isValueNotInOptions && currentValue) {
        if (isEnumDataField) {
          setValue(fieldParameter.key, null, { shouldDirty: false });
        } else {
          resetField(fieldParameter.key, { keepDirty: false });
        }
      }
    } catch (error) {
      console.error(
        `[FormEngine:TemplateField] Error refreshing options:`,
        error
      );
    }

    return () => {
      isMounted = false;
    };
  }, [
    fetchOptionsData,
    getValues,
    fieldParameter.key,
    resetField,
    setValue,
    isEnumDataField,
  ]);

  useEffect(() => {
    if (isEnumDataField && fetchOptionsData) {
      fetchOptionsData();
    }
  }, [isEnumDataField, fetchOptionsData]);

  useEffect(() => {
    refetchAfterChange();
  }, [determinedByValues, refetchAfterChange, fieldParameter.key]);

  const handleClear = useCallback(() => {
    setValue(fieldParameter.key, null, { shouldDirty: true });
  }, [setValue, fieldParameter.key]);

  return (
    <FieldRenderer
      fieldParameter={fieldParameter}
      externalResources={externalResources}
      options={isEnumDataField ? enumOptions : []}
      isLoading={isEnumDataField && isLoading}
      onClear={handleClear}
    />
  );
};

export default React.memo(TemplateField);
